import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { withRouter } from 'react-router';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import filterFp from 'lodash/fp/filter';
import pipeFp from 'lodash/fp/pipe';
import sliceFp from 'lodash/fp/slice';

import { withMarketplaceContext } from '@nerdwallet/structured-content/contexts/MarketplaceContext';
import {
  Container as ABContainer,
  Variant,
  Control,
} from '@nerdwallet/nw-client-lib/ab';
import { CompareCardSticky } from '@nerdwallet/compare-credit-cards-bar';

import {
  Experiment,
  removeControlOrVariantCardByCategory,
} from '../../lib/experiment-list';
import { OfferSDK } from '../../lib/nw-api-sdk';
import withDataDependency from '../../lib/with-data-dependency-wrapper';

import CreditCardBox from '../credit-card-box/credit-card-box';
import Row from '../row/row';
import NoCardsFound from '../no-cards-found/no-cards-found';
import LoadingSpinner from '../loading-spinner/loading-spinner';
import { getQueryParams, updateParams } from '../../lib/query-param-helpers';
import {
  getOffersByIds,
  unpackStringIds,
  validateOffer,
} from '../../lib/helpers';
import { DATA_DEPENDENCY_TYPES } from '../../lib/constants';
import {
  adminCategoryPageSelectInput,
  dataDependencyTypePicker,
  dataDependencyCommaSeparatedStringInput,
  numberInputSpec,
  repeatable,
  stringSelectSpec,
  boolCheckboxSpec,
} from '../../lib/editing-configs';
import withOutOfDateMessaging from '../with-out-of-date-messaging/with-out-of-date-messaging';

const getLegacyId = _.property('details.legacy_id.value');

const CreditCardTable = (props) => {
  const {
    data,
    drivers,
    resultSizeLimit,
    dispatch,
    history,
    adminCategoryPage,
    featuredFirstCard,
    openFirstCardDrawers,
    experiments,
    useExperiments,
    hideProsAndCons,
    products,
    requestedIds,
  } = props;

  const loading = _.get(data, 'loading');

  const queryParams = getQueryParams(history);
  const categoryName = _.get(queryParams, 'category', 'default');

  // swap cards out for various A/B experiments
  if (useExperiments) {
    removeControlOrVariantCardByCategory({
      experiment: Experiment.MARKETPLACE_CAP_ONE,
      experiments,
      category: categoryName,
      cardIDs: requestedIds,
    });
  }

  const offers = getOffersByIds({
    ids: requestedIds,
    offers: _.values(products),
  });

  const applyCardLimit = sliceFp(0, resultSizeLimit);

  const cardsToBoxes = (cards) =>
    cards.map((offer, index) => {
      const id = getLegacyId(offer);
      return (
        <Row key={id} className="credit-card-box__card-wrapper">
          <CreditCardBox
            sectionPosition={index}
            productPosition={index}
            featuredCard={featuredFirstCard && index === 0}
            defaultOpen={openFirstCardDrawers && index === 0}
            offer={offer}
            drivers={drivers}
            experiments={experiments}
            hideProsAndCons={hideProsAndCons}
          />
        </Row>
      );
    });

  const displayCards = (cards) => {
    if (cards.length === 0 && !loading) {
      return (
        <NoCardsFound
          resetFilters={() =>
            dispatch(updateParams({ category: queryParams.category }, history))
          }
        />
      );
    }

    return (
      <div style={{ position: 'relative', minHeight: '300px' }}>
        <LoadingSpinner show={loading} />
        {cardsToBoxes(cards)}
      </div>
    );
  };

  const filteredOffers = pipeFp(
    filterFp(validateOffer(queryParams, adminCategoryPage))
  )(offers);

  const controlDisplay = pipeFp(applyCardLimit, displayCards);
  const controlOffers = controlDisplay(filteredOffers);

  return (
    <div data-integration="credit-card-table" key={categoryName}>
      <CompareCardSticky offerSDK={OfferSDK} />
      <ABContainer experimentID={Experiment.MARKETPLACE_CAP_ONE.id}>
        <Control />
        <Variant
          variantName={Experiment.MARKETPLACE_CAP_ONE.variant.ALTERNATE_OFFER_1}
        />
        <Variant
          variantName={Experiment.MARKETPLACE_CAP_ONE.variant.ALTERNATE_OFFER_2}
        />
      </ABContainer>
      <ABContainer experimentID={Experiment.MARKETPLACE_MOBILE_MOVE_CTA.id}>
        <Control />
        <Variant
          variantName={Experiment.MARKETPLACE_MOBILE_MOVE_CTA.variant.ON}
        />
      </ABContainer>
      {controlOffers}
    </div>
  );
};

CreditCardTable.propTypes = {
  products: PropTypes.object,
  drivers: PropTypes.arrayOf(
    PropTypes.shape({
      driver: PropTypes.string,
    })
  ),
  adminCategoryPage: PropTypes.string,
  resultSizeLimit: PropTypes.number,
  loading: PropTypes.bool,
  dispatch: PropTypes.func,
  history: PropTypes.object,
  requestedIds: PropTypes.arrayOf(PropTypes.number),
  featuredFirstCard: PropTypes.bool,
  openFirstCardDrawers: PropTypes.bool,
  dataDependencyType: PropTypes.string,
  productPage: PropTypes.string,
  experiments: PropTypes.object,
  useExperiments: PropTypes.bool,
  hideProsAndCons: PropTypes.bool,
  data: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    error: PropTypes.object,
    value: PropTypes.object,
  }),
  dataDependency: PropTypes.shape({
    data: PropTypes.shape({
      ids: PropTypes.string,
      productPage: PropTypes.string,
    }),
    type: PropTypes.string,
  }),
};

CreditCardTable.defaultProps = {
  products: {},
  drivers: [
    { driver: 'annual_fee' },
    { driver: 'bonus_value' },
    { driver: 'intro_APR' },
    { driver: 'ongoing_APR' },
    { driver: 'credit_score' },
  ],
  resultSizeLimit: 100,
  featuredFirstCard: false,
  openFirstCardDrawers: false,
  loading: false,
  requestedIds: [],
  dataDependencyType: '',
  productPage: '',
  experiments: {},
  useExperiments: false,
  hideProsAndCons: false,
  data: {},
  dataDependency: {},
};

CreditCardTable.getEditingConfig = () => {
  const dataDependencyConfig = [
    dataDependencyCommaSeparatedStringInput,
    numberInputSpec('cardLimit', 'Number of Cards Showing', {
      help: 'Only this number of cards will display on the page. If user applies or removes filters, this ensures that the number of cards on the page stays the same.',
    }),
  ];

  return [
    dataDependencyTypePicker,
    adminCategoryPageSelectInput,
    ...dataDependencyConfig,
    repeatable('drivers', 'Drivers', 'Driver', () => [
      stringSelectSpec('driver', '', [
        { label: '-- Choose a driver --', value: '' },
        { label: 'Annual Fee', value: 'annual_fee' },
        { label: 'Bonus Value', value: 'bonus_value' },
        { label: 'Intro Purchase APR', value: 'intro_APR' },
        { label: 'Intro APR (Full Text)', value: 'intro_APR_full_text' },
        { label: 'Intro Purchase APR (no duration)', value: 'intro_APR_only' },
        { label: 'Intro Purchase APR Period', value: 'intro_APR_period' },
        { label: 'Ongoing APR', value: 'ongoing_APR' },
        { label: 'Recommended Credit Score', value: 'credit_score' },
        { label: 'Rewards', value: 'rewards' },
        { label: 'Intro Balance Transfer APR', value: 'intro_BT_APR' },
        {
          label: 'Intro Balance Transfer APR Period',
          value: 'intro_BT_APR_period',
        },
        { label: 'Balance Transfer Fee', value: 'balance_transfer_fee' },
        { label: 'Minimum Deposit', value: 'minimum_deposit' },
      ]),
    ]),
    boolCheckboxSpec('featuredFirstCard', 'Featured first card?'),
    boolCheckboxSpec('openFirstCardDrawers', 'Open first card drawers?'),
    boolCheckboxSpec('hideProsAndCons', 'Hide pros and cons section?'),
  ];
};

const dataDependencies = (props) => ({
  data: {
    type: _.get(
      props,
      'dataDependency.type',
      DATA_DEPENDENCY_TYPES.CREDIT_CARDS
    ),
    ids: unpackStringIds(_.get(props, 'dataDependency.data.ids', '')),
    productPage: _.get(props, 'dataDependency.data.productPage'),
  },
});

const WrappedComponent = compose(
  connect(
    createStructuredSelector({
      experiments: _.property('experiments'),
    })
  ),
  withRouter,
  withMarketplaceContext,
  withOutOfDateMessaging
)(CreditCardTable);

export default withDataDependency(WrappedComponent, dataDependencies);
